import React from 'react';
import { DoodleBodyParts } from './parts/body';
import { DoodleAccessoryKey, DoodleAccessoryParts } from './parts/accessories';
import { DoodleEyesParts } from './parts/eyes';
import { DoodleMouthParts } from './parts/mouth';
import Doodle, { DEFAULT_DOODLE_CONFIG, DoodleColors, DoodleConfig } from './doodle';
import { FlexDiv } from '../../../containers_v2/products/style';
import styled from 'styled-components';
import { Ban, Car, Palette } from 'lucide-react';
import { CustomIconProperties } from '../custom-icons';
import { useFunctionState } from '../../../utils/customHooks';
import { PrimaryColor, SidelyBlack, SurfaceColor } from '../../global/css/Utils';
import { TitleFontFamily } from '../../global/css/GlobalText';
const PAGE_HEIGHT = '400px';
const PASSPORT_WIDTH = '600px';
const PAGE_COLOR = '#dfdac2';
const PASSPORT_ICON_COLOR = '#c0ac8d';
const PASSPOT_ICON_SIZE = 45;
const PASSPORT_BORDER_COLOR = '#c9bf9f';
const THUMBNAIL_SIZE = 46;
const TAB_ICON_SIZE = 30;

// Area of the shared 240x240 doodle frame shown in a thumbnail, so small parts are zoomed in
type Crop = { x: number, y: number, size: number };
const BODY_CROP: Crop = { x: 36, y: 60, size: 186 };
const EYES_CROP: Crop = { x: 75, y: 83, size: 90 };
const MOUTH_CROP: Crop = { x: 88, y: 133, size: 64 };
const ACCESSORY_CROPS: Record<DoodleAccessoryKey, Crop> = {
	'casque': { x: 39, y: 64, size: 162 },
	'coeur': { x: 164, y: 26, size: 72 },
	'dodo': { x: 160, y: 21, size: 75 },
	'eyepatch': { x: 71, y: 92, size: 60 },
	'hat': { x: 15, y: -31, size: 210 },
	'lunettes': { x: 69, y: 77, size: 103 },
	'moustache': { x: 92, y: 120, size: 57 },
	'parapluie': { x: 50, y: -36, size: 161 },
	'police-hat': { x: 45, y: -7, size: 150 },
	'stars': { x: 164, y: 26, size: 71 },
	'sunglasses': { x: 75, y: 83, size: 91 },
	'toque': { x: 44, y: -19, size: 152 }
};

type Tab = keyof DoodleConfig;
const TABS: { key: Tab, icon: React.ReactNode }[] = [
	{ key: 'bodyColor', icon: <Palette size={TAB_ICON_SIZE} color={SidelyBlack} /> },
	{ key: 'body', icon: <PartThumbnail part={DoodleBodyParts['corps-1']} crop={BODY_CROP} size={TAB_ICON_SIZE} /> },
	{ key: 'eyes', icon: <PartThumbnail part={DoodleEyesParts['yeux-1']} crop={EYES_CROP} size={TAB_ICON_SIZE} /> },
	{ key: 'mouth', icon: <PartThumbnail part={DoodleMouthParts['bouche-1']} crop={MOUTH_CROP} size={TAB_ICON_SIZE} /> },
	{ key: 'accessory', icon: <PartThumbnail part={DoodleAccessoryParts['hat']} crop={ACCESSORY_CROPS['hat']} size={TAB_ICON_SIZE} /> }
];

export default function DoodleCreator(props: {
	config?: DoodleConfig,
	onChange?: (config: DoodleConfig) => void,
	name?: string,
	email?: string,
	phone?: string,
	backgroundColor?: string
}) {
	const [config, setConfig, setConfigNoUpdateParent] = useFunctionState(DEFAULT_DOODLE_CONFIG, undefined, props.onChange);
	const [tab, setTab] = React.useState<Tab>('body');
	const [opened, setOpened] = React.useState(false);
	const set = <K extends keyof DoodleConfig>(key: K) => (value: DoodleConfig[K]) => setConfig(config => ({ ...config, [key]: value }));
	const bodyColor = DoodleColors[config.bodyColor];

	React.useEffect(() => {
		// configs saved before bodyColor existed get the default white
		setConfigNoUpdateParent({ ...DEFAULT_DOODLE_CONFIG, ...props.config });
	}, [props.config]);

	return <div>
		<PassportBackground>
			<PassportCover opened={opened} onClick={() => setOpened(true)}>
				<Car fill={PAGE_COLOR} color={PrimaryColor} size={PASSPOT_ICON_SIZE * 2} />
				<PassportCoverTitle>PASSPORT</PassportCoverTitle>
			</PassportCover>
			<PassportPage>
				<PassportPageContent>
					<FlexDiv flow='column' gap='5px'>
						<Car fill={PASSPORT_ICON_COLOR} color={PAGE_COLOR} size={PASSPOT_ICON_SIZE} />
						<PassportTitle>PASSPORT</PassportTitle>
					</FlexDiv>
					<PassportInformationsWrapper>
						<PassportInformation>
							<PassportInformationTitle>
								Name
							</PassportInformationTitle>
							<PassportInformationValue>
								{props.name}
							</PassportInformationValue>
						</PassportInformation>
						<PassportInformation />
						<PassportInformation>
							<PassportInformationTitle>
								Email
							</PassportInformationTitle>
							<PassportInformationValue>
								{props.email}
							</PassportInformationValue>
						</PassportInformation>
						<PassportInformation>
							<PassportInformationTitle>
								Phone
							</PassportInformationTitle>
							<PassportInformationValue>
								{props.phone}
							</PassportInformationValue>
						</PassportInformation>
					</PassportInformationsWrapper>
					<DoodleImageWrapperWrapper>
						<DoodleImageWrapper backgroundColor={props.backgroundColor}>
							<Doodle width='100%' height='100%' config={config} />
						</DoodleImageWrapper>
					</DoodleImageWrapperWrapper>
					<Picker>
						<Tabs>
							{TABS.map(t => <TabButton key={t.key} selected={tab === t.key} onClick={() => setTab(t.key)}>
								{t.icon}
							</TabButton>)}
						</Tabs>
						<OptionsPanel>
							{tab === 'bodyColor' && <OptionGrid
								value={config.bodyColor}
								options={DoodleColors}
								onSelect={set('bodyColor')}
								render={color => <PartThumbnail part={DoodleBodyParts[config.body]} crop={BODY_CROP} bodyColor={DoodleColors[color]} />}
							/>}
							{tab === 'body' && <OptionGrid
								value={config.body}
								options={DoodleBodyParts}
								onSelect={set('body')}
								render={body => <PartThumbnail part={DoodleBodyParts[body]} crop={BODY_CROP} bodyColor={bodyColor} />}
							/>}
							{tab === 'eyes' && <OptionGrid
								value={config.eyes}
								options={DoodleEyesParts}
								onSelect={set('eyes')}
								render={eyes => <PartThumbnail part={DoodleEyesParts[eyes]} crop={EYES_CROP} />}
							/>}
							{tab === 'mouth' && <OptionGrid
								value={config.mouth}
								options={DoodleMouthParts}
								onSelect={set('mouth')}
								render={mouth => <PartThumbnail part={DoodleMouthParts[mouth]} crop={MOUTH_CROP} />}
							/>}
							{tab === 'accessory' && <OptionGrid
								nullable
								value={config.accessory}
								options={DoodleAccessoryParts}
								onSelect={set('accessory')}
								render={accessory => <PartThumbnail part={DoodleAccessoryParts[accessory]} crop={ACCESSORY_CROPS[accessory]} />}
							/>}
						</OptionsPanel>
					</Picker>
					<BottomText>
						{'P>Jhon>>Doe>>>>>>12341231234123123>>12312341265754430>>>>>>>>>>>>>0948921937400>>4233588942384'}
					</BottomText>
				</PassportPageContent>
			</PassportPage>
		</PassportBackground>
	</div>;
}

function PartThumbnail(props: { part: React.ComponentType<CustomIconProperties & { bodyColor?: string }>, crop: Crop, size?: number, bodyColor?: string }) {
	const size = props.size ?? THUMBNAIL_SIZE;
	const scale = size / props.crop.size;
	const Part = props.part;
	return <ThumbnailClip size={size}>
		<Part
			size={240 * scale}
			bodyColor={props.bodyColor}
			style={{ position: 'absolute', left: -props.crop.x * scale, top: -props.crop.y * scale }}
		/>
	</ThumbnailClip>;
}

type OptionGridProps<K extends string> =
	| { value: K, onSelect: (value: K) => void, options: Record<K, unknown>, render: (key: K) => React.ReactNode, nullable?: false }
	| { value: K | undefined, onSelect: (value: K | undefined) => void, options: Record<K, unknown>, render: (key: K) => React.ReactNode, nullable: true };

function OptionGrid<K extends string>(props: OptionGridProps<K>) {
	const keys = Object.keys(props.options) as K[];
	const onSelect = props.onSelect as (value: K | undefined) => void;
	return <Grid>
		{props.nullable && <OptionTile type='button' selected={props.value === undefined} onClick={() => onSelect(undefined)}>
			<Ban size={THUMBNAIL_SIZE * 0.6} color={PASSPORT_ICON_COLOR} />
		</OptionTile>}
		{keys.map(key => <OptionTile type='button' key={key} selected={props.value === key} onClick={() => onSelect(key)}>
			{props.render(key)}
		</OptionTile>)}
	</Grid>;
}

const ThumbnailClip = styled.div<{ size: number }>`
	position: relative;
	overflow: hidden;
	width: ${p => p.size}px;
	height: ${p => p.size}px;
	flex-shrink: 0;
`;

const Picker = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

const Tabs = styled.div`
	display: flex;
	gap: 6px;
	padding: 0 6px;
	border-bottom: 2px solid ${PASSPORT_BORDER_COLOR};
`;

const TabButton = styled.button<{ selected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 42px;
	border: 2px solid ${PASSPORT_BORDER_COLOR};
	border-bottom: none;
	border-radius: 10px 10px 0 0;
	cursor: pointer;
	position: relative;
	${p => p.selected ? `
		background-color: ${PAGE_COLOR};
		height: 48px;
		margin-top: -6px;
		bottom: -2px;
	` : `
		background-color: ${PASSPORT_ICON_COLOR};
		border-color: ${PASSPORT_ICON_COLOR};
		> * { opacity: 0.5; }
	`}
`;

const OptionsPanel = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 10px 6px;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, ${THUMBNAIL_SIZE + 8}px);
	gap: 6px;
`;

const OptionTile = styled.button<{ selected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${THUMBNAIL_SIZE + 8}px;
	height: ${THUMBNAIL_SIZE + 8}px;
	padding: 0;
	border-radius: 8px;
	cursor: pointer;
	background-color: ${PAGE_COLOR};
	border: 2px solid ${p => p.selected ? 'white' : PASSPORT_BORDER_COLOR};
	box-shadow: ${p => p.selected ? `0 0 0 1px ${PASSPORT_BORDER_COLOR}` : 'none'};
	&:hover {
		border-color: ${p => p.selected ? 'white' : PASSPORT_ICON_COLOR};
	}
`;

const PassportBackground = styled.div`
	position: relative;
	width: ${PASSPORT_WIDTH};
	overflow: hidden;
	padding: 10px;
	background-color: ${PrimaryColor};
`;

// Lies on the page until clicked, then flips up around the page's top edge and vanishes once past edge-on
const PassportCover = styled.div<{ opened: boolean }>`
	position: absolute;
	inset: 10px;
	z-index: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 10px;
	border-radius: 0 0 10px 10px;
	background-color: ${PrimaryColor};
	border: 2px solid color-mix(in oklab, ${PrimaryColor}, ${SidelyBlack} 15%);
	transform-origin: top center;
	transform: perspective(1500px) rotateX(${p => p.opened ? 180 : 0}deg);
	transition: transform 1s cubic-bezier(0.65, 0, 0.35, 1);
	backface-visibility: hidden;
	cursor: pointer;
	pointer-events: ${p => p.opened ? 'none' : 'auto'};
	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`;

const PassportCoverTitle = styled.h6`
	font-size: 28px;
	font-weight: 700;
	font-family: ${TitleFontFamily};
	letter-spacing: 4px;
	margin: 0;
	color: ${PAGE_COLOR};
`;

const PassportPage = styled.div`
	border-radius: 10px;
	padding: 10px;
	height: ${PAGE_HEIGHT};
	background: linear-gradient(180deg, color-mix(in oklab, ${PAGE_COLOR}, ${SidelyBlack} 10%) 0%, ${PAGE_COLOR} 10%);
`;

const PassportPageContent = styled.div`
	background-color: #d2ccb2;
	border-radius: 0 0 10px 10px;
	display: grid;
	grid-template-rows: auto 240px 1fr;
	grid-template-columns: 170px 1fr;
	gap: 10px;
	height: 100%;

	background-size: 10px 10px;
	background-image: radial-gradient(${PAGE_COLOR} 1.5px, transparent 0);
	padding: 10px 20px;
`;

const DoodleImageWrapperWrapper = styled.div`
	background-color: #5397dd;
	border-radius: 10px;
	padding: 10px;
	height: 100%;
	width: 100%;
	background-color: ${PAGE_COLOR};
`;

const DoodleImageWrapper = styled.div<{ backgroundColor?: string }>`
	border-radius: 10px;
	height: 100%;
	width: 100%;
	background-color: ${p => p.backgroundColor ?? SurfaceColor};
`;
const PassportTitle = styled.h6`
	font-size: 20px;
  font-weight: 700;
  font-family: ${TitleFontFamily};
  margin: 0;
  color: ${PrimaryColor};
`;

const PassportInformationsWrapper = styled.div`
	display: grid;
	grid-template-rows: 1fr 1fr;
	grid-template-columns: 1fr 1fr;
`;

const PassportInformation = styled.div`
`;

const PassportInformationTitle = styled.div`
	font-size: 11px;
	font-family: ${TitleFontFamily};
	color: ${PrimaryColor};
	font-weight: 700;
`;
const PassportInformationValue = styled.div`
	font-size: 13px;
	font-family: ${TitleFontFamily};
	color: ${PASSPORT_ICON_COLOR};
	font-weight: 700;
`;


const BottomText = styled(PassportInformationTitle)`
	grid-column: 1 / 3;
   	align-items: center;
    display: flex;
    overflow: hidden;
`;
